import React, { useCallback, useState } from 'react';
import { atom, useAtom } from 'jotai';
import { Box } from '@dropbox/dig-foundations';
import { SignerDeclineModal } from 'signer-app/signer-decline-modal/signer-decline-modal';
import '@dropbox/dpc-ia/index.css';
import {
  useSignerAppParams,
  useSignerContext,
} from 'signer-app/signer-experience/signer-experience';
import { redirectTo } from 'signer-app/utils/redirect';
import { useHistory } from 'react-router-dom';
import { ReassignModal } from 'signer-app/pages/reassign-complete/reassign-modal';
import { TestModeModal } from 'signer-app/signer-experience/test-mode-modal';
import { SignerEIdModal } from 'signer-app/signer-eid-modal/signer-eid-modal';
import { useLocation } from 'react-router';
import { ToolbarMenu } from 'signer-app/signer-experience/signer-toolbar/toolbar-menu';
import { ToolbarContent } from 'signer-app/signer-experience/signer-toolbar/toolbar-content';
import { useBundleProps } from 'signer-app/bundle-props';
import { SignerToolbarBanner } from 'signer-app/signer-experience/signer-toolbar/signer-toolbar-banner';
import { PreviewLogoBar } from 'signer-app/signer-experience/signer-toolbar/preview-logo-bar';

const reassignOnLoadHandledAtom = atom(false);

export function SignerToolbar({
  showNextButton = true,
}: {
  showNextButton?: boolean;
}) {
  const { step, signatureRequestId } = useSignerAppParams();
  const { embeddedData, showReassignOnLoad } = useBundleProps();
  const [modals, setModals] = useState({
    decline: false,
    reassign: false,
  });
  const [reassignOnLoadHandled, setReassignOnLoadHandled] = useAtom(
    reassignOnLoadHandledAtom,
  );

  const {
    allowReassign,
    accessCode,
    transmissionGuid,
    signatureRequest,
    isMobileReady,
    hideFormViewToggle,
    isComingFromSmsDelivery,
  } = useSignerContext();
  const [showTestMode, setShowTestMode] = useState(!!signatureRequest.testMode);
  const isFormViewAvailable = !hideFormViewToggle && isMobileReady;

  React.useEffect(() => {
    if (!showReassignOnLoad || reassignOnLoadHandled) {
      return;
    }
    setReassignOnLoadHandled(true);
    if (allowReassign) {
      setModals({ decline: false, reassign: true });
    }
  }, [
    allowReassign,
    reassignOnLoadHandled,
    setReassignOnLoadHandled,
    showReassignOnLoad,
  ]);

  const history = useHistory();
  const location = useLocation();
  const searchParams = React.useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const modalToggle = useCallback(
    (modalName: keyof typeof modals) => () => {
      setModals({
        ...modals,
        [modalName]: !modals[modalName],
      });
    },
    [modals, setModals],
  );
  const toggleDecline = modalToggle('decline');

  return (
    <Box data-testid="signer-toolbar">
      {step === 'preview' && <PreviewLogoBar />}
      <SignerToolbarBanner setModals={setModals} />
      <ToolbarContent
        showNextButton={showNextButton}
        toolbarMenu={
          <ToolbarMenu
            isLarge={step === 'preview' && isFormViewAvailable}
            onReassign={() => {
              setModals({ reassign: true, decline: false });
            }}
            onDecline={toggleDecline}
          />
        }
      />
      <ReassignModal
        tsmGuid={transmissionGuid}
        signatureRequestId={signatureRequestId}
        accessCode={accessCode ?? undefined}
        isOpen={modals.reassign}
        closeModal={() => {
          setModals((prev) => ({ ...prev, reassign: false }));
        }}
      />
      <TestModeModal
        showTestMode={showTestMode}
        setShowTestMode={setShowTestMode}
      />
      {modals.decline && (
        <SignerDeclineModal
          isEmbedded={embeddedData?.isEmbeddedSigning}
          transmissionGuid={transmissionGuid}
          accessCode={accessCode}
          parentUrl={embeddedData?.parentUrl}
          signatureId={embeddedData?.signatureId}
          isComingFromSmsDelivery={isComingFromSmsDelivery}
          onRequestClose={toggleDecline}
          onRequestClosed={() => {
            history.replace(`/sign/${signatureRequestId}/requestClosed`);
          }}
          onDeclineSuccess={() => {
            if (embeddedData?.isEmbeddedSigning) {
              // in-app navigation
              history.push(`/sign/${signatureRequestId}/declineComplete`);
            } else {
              // full page navigation
              redirectTo('/info/externalNothingToSign');
            }
          }}
        />
      )}
      <SignerEIdModal
        isOpen={searchParams.has('qes')}
        onRequestClose={() => history.goBack()}
      />
    </Box>
  );
}
